/**
 * ModerationCaseCardHeader Component
 *
 * Header for moderation case cards with metadata display.
 * Shows Subject ID | Username | Timestamp with pipe separators in a single
 * left-aligned row. Renders a right-aligned vertical dots action menu trigger
 * button alongside the metadata.
 */

import { IconSize, BadgeVariant, Badge, Text, IconButton, IconButtonSize, MoreVertical, ButtonType } from '@soteria/design-system-ve';

import * as styles from './ModerationCaseCard.css';

/** Resolves a nullable display value, falling back to 'N/A' */
function displayOrNA(value?: string | null): string {
  return value || 'N/A';
}

interface MetadataAriaParams {
  userId?: string | null;
  username?: string | null;
  fullTimestamp?: string | null;
  timestamp?: string | null;
}

/** Builds the accessible description for the metadata row */
function buildMetadataAriaLabel({ userId, username, fullTimestamp, timestamp }: MetadataAriaParams): string {
  return `Subject ID: ${displayOrNA(userId)}, User: ${displayOrNA(username)}, Time: ${displayOrNA(fullTimestamp ?? timestamp)}`;
}

/** Renders the selection checkbox when enabled */
function renderCheckbox(showCheckbox: boolean, isChecked: boolean) {
  if (!showCheckbox) return null;

  return (
    <input
      readOnly
      aria-label={isChecked ? 'Case selected' : 'Case not selected'}
      checked={isChecked}
      className={styles.headerCheckbox}
      tabIndex={-1}
      type="checkbox"
    />
  );
}

export interface ModerationCaseCardHeaderProps {
  /**
   * Segment name (optional)
   */
  segmentName?: string | null;

  /**
   * Username (optional)
   */
  username?: string | null;

  /**
   * User/Subject ID (optional)
   */
  userId?: string | null;

  /**
   * Formatted timestamp string for display (optional)
   */
  timestamp?: string | null;

  /**
   * Full timestamp string for tooltip display (optional)
   */
  fullTimestamp?: string | null;

  /**
   * Additional CSS class name
   */
  className?: string;

  /**
   * Whether to show the action menu trigger button ("...")
   * @default false
   */
  showActionMenu?: boolean;

  /**
   * ID for the action menu trigger button (links to Menu via triggerId for accessibility)
   */
  actionMenuTriggerId?: string;

  /**
   * Handler when action menu button is clicked
   */
  onActionMenuToggle?: () => void;

  /**
   * Whether to display a visual checkbox indicating focused/selected state.
   * @default false
   */
  showCheckbox?: boolean;

  /**
   * Whether the checkbox is checked (card is selected/focused).
   * Only relevant when showCheckbox is true.
   */
  isChecked?: boolean;
}

/**
 * Header component for ModerationCaseCard
 *
 * Renders metadata in "Subject ID | Username | Timestamp" format with pipe
 * separators and an optional segment badge.
 *
 * @example
 * ```tsx
 * <ModerationCaseCardHeader
 *   segmentName="Screenshots"
 *   username="user123"
 *   userId="abc12345"
 *   timestamp="Jan 21, 2026"
 *   fullTimestamp="Jan 21, 2026, 3:45:12 PM"
 *   showActionMenu
 *   actionMenuTriggerId="moderation-action-menu-trigger"
 *   onActionMenuToggle={() => setIsActionMenuOpen(prev => !prev)}
 * />
 * ```
 */
export function ModerationCaseCardHeader({
  segmentName,
  username,
  userId,
  timestamp,
  fullTimestamp,
  className,
  showActionMenu = false,
  actionMenuTriggerId,
  onActionMenuToggle,
  showCheckbox = false,
  isChecked = false,
}: ModerationCaseCardHeaderProps) {
  const containerClassName = `${styles.headerContainer} ${className ?? ''}`.trim();
  const ariaLabel = buildMetadataAriaLabel({ userId, username, fullTimestamp, timestamp });

  return (
    <div className={containerClassName}>
      {renderCheckbox(showCheckbox, isChecked)}

      {/* Metadata Row — Subject ID | Username | Timestamp */}
      <div className={styles.metadataRow}>
        {segmentName && <Badge variant={BadgeVariant.Default}>{segmentName}</Badge>}
        <div aria-label={ariaLabel} className={styles.metadataItem} role="note" title={fullTimestamp ?? undefined}>
          <Text size="xs">{displayOrNA(userId)}</Text>
          <Text aria-hidden="true" className={styles.metadataSeparator} size="xs">
            |
          </Text>
          <Text size="xs">{displayOrNA(username)}</Text>
          <Text aria-hidden="true" className={styles.metadataSeparator} size="xs">
            |
          </Text>
          <Text size="xs">{displayOrNA(timestamp)}</Text>
        </div>

        {/* Action Menu Trigger Button — right-aligned via marginLeft auto */}
        {showActionMenu && onActionMenuToggle && (
          <div className={styles.actionMenuTrigger}>
            <IconButton
              aria-label="More actions"
              buttonSize={IconButtonSize.Small}
              buttonType={ButtonType.Secondary}
              icon={MoreVertical}
              iconSize={IconSize.Small}
              id={actionMenuTriggerId}
              title="More actions"
              onClick={e => {
                e.stopPropagation();
                onActionMenuToggle();
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
